import { useEffect, useState } from 'react'

/**
 * How often an open tab re-checks whether a timed status has elapsed.
 *
 * A status is cosmetic, so being up to half a minute late to hide one is fine —
 * and one coarse shared interval is far cheaper than a timer per row.
 */
const STATUS_TICK_MS = 30_000

/**
 * One interval for the whole tab, shared by every consumer.
 *
 * It used to be an interval per mounted component, which was defensible while
 * only a member list called it. It is not now: the status emoji moved onto the
 * name line, so a sidebar with N people renders N `AimStatusEmoji`s, each of
 * which resolves expiry — and each of which would otherwise own a timer and
 * re-render on its own stagger. The subscriber set collapses that to one timer
 * and one instant, which is also what makes "every row agrees about now" true
 * rather than merely likely.
 *
 * The timer exists only while something is subscribed, so a tab with no status
 * on screen runs none at all.
 */
const subscribers = new Set<(now: Date) => void>()
let timerId: ReturnType<typeof setInterval> | null = null
let sharedNow = new Date()

/**
 * The shared instant, refreshed first if it has gone stale.
 *
 * Staleness is reachable: with no subscribers there is no timer, so `sharedNow`
 * freezes at the last tick. A component mounting after a quiet spell would
 * otherwise resolve expiry against an instant minutes in the past and show a
 * status that has already lapsed.
 */
const currentTick = (): Date => {
  if (Date.now() - sharedNow.getTime() >= STATUS_TICK_MS) sharedNow = new Date()
  return sharedNow
}

const subscribe = (onTick: (now: Date) => void): (() => void) => {
  subscribers.add(onTick)

  timerId ??= setInterval(() => {
    sharedNow = new Date()
    subscribers.forEach((notify) => notify(sharedNow))
  }, STATUS_TICK_MS)

  return () => {
    subscribers.delete(onTick)
    if (subscribers.size > 0 || !timerId) return
    clearInterval(timerId)
    timerId = null
  }
}

/**
 * A "now" that advances every `STATUS_TICK_MS`, for resolving status expiry.
 *
 * Expiry cannot come from the store. Nothing emits an event when a status
 * elapses — it is a fact about the clock — and a reselect selector memoized on
 * an unchanged user object can never turn a live status into an expired one no
 * matter how often it is read. So the component has to be told time passed:
 *
 *   const now = useAimStatusTick()
 *   const status = useMemo(() => liveAimStatus(raw, now), [raw, now])
 *
 * Returns a `Date` rather than a counter deliberately. A counter would be an
 * unused dependency of that `useMemo` — correct in effect but flagged by
 * `react-hooks/exhaustive-deps`, and only "obviously" load-bearing to whoever
 * wrote it. Handing back the instant makes the dependency real: the value is
 * what the comparison is against.
 *
 * Every caller in the tab shares one timer and one instant, so calling this per
 * row is fine — see the subscriber set above.
 */
export const useAimStatusTick = (): Date => {
  const [now, setNow] = useState(currentTick)

  useEffect(() => subscribe(setNow), [])

  return now
}

export default useAimStatusTick
